import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import 'chartjs-adapter-date-fns';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import {
  IThematicInnovationActivityPoint,
  IThematicInnovationMetricKey,
} from '../../../../../../core/models/results.model';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { LoadingProgressComponent, LoadingStage } from '../../../../../../shared/components/loading-progress/loading-progress.component';

const LOADING_STAGES: LoadingStage[] = [
  { text: 'Scanning inventions across the theme…', atSeconds: 0 },
  { text: 'Aligning 12-month and 10-year windows…', atSeconds: 8 },
  { text: 'Computing growth metrics…', atSeconds: 20 },
];

type MetricConfig = {
  label: string;
  color: string;
  chartType: 'bar' | 'scatter';
  yTitle: string;
  hoverSuffix?: string;
  opacity?: number;
};

const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const GROWTH_METRIC: IThematicInnovationMetricKey = 'inv_growth_relative_12m_sia';

// "2026-08-31" -> "Aug 2026"
const formatMonthYear = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })} ${d.getUTCFullYear()}`;
};

const COLOR = {
  axisText: '#64748b',
  axisTitle: '#64748b',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(10, 16, 31, 0.12)',
  zeroLine: 'rgba(15, 23, 42, 0.3)',
  chartAreaBg: '#ffffff',
};

const METRIC_OPTIONS: Record<IThematicInnovationMetricKey, MetricConfig & { footnote: string }> = {
  inv_growth_absolute_12m: {
    label: 'Abs. Growth (Inventions, 12m)',
    color: '#a2c613',
    chartType: 'bar',
    yTitle: 'Inventions (12m)',
    hoverSuffix: ' Inventions',
    footnote: 'the latest year uses a rolling last 12 months period',
  },
  inv_growth_relative_12m_sia: {
    label: 'Rel. Growth (% YoY, 12m)',
    color: '#f59e0b',
    chartType: 'bar',
    yTitle: 'Relative Growth (%)',
    hoverSuffix: '%',
    footnote: 'the last year uses the current month for the calculation',
  },
  invention_count_120m: {
    label: 'Size (Cumulative, 10y)',
    color: '#22c55e',
    chartType: 'bar',
    yTitle: 'Inventions (10y)',
    hoverSuffix: ' Inventions',
    footnote: 'the latest year uses a rolling last 120 months period',
  },
};

// Paints a white background so PNG exports aren't transparent.
const whiteBackgroundPlugin = {
  id: 'innovActivityWhiteBg',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// Paints a subtle tint inside the plot area so the chart reads as a distinct region.
// Must run in beforeDraw (before scales draw) so gridlines paint on top, not under.
const chartAreaBackgroundPlugin = {
  id: 'innovActivityChartAreaBg',
  beforeDraw(chart: Chart) {
    const { left, top, width, height } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.fillStyle = COLOR.chartAreaBg;
    ctx.fillRect(left, top, width, height);
    ctx.restore();
  },
};

// Strokes a horizontal line at y=0 when the active metric opts in.
const zeroLinePlugin = {
  id: 'innovActivityZeroLine',
  afterDatasetsDraw(chart: Chart, _args: any, opts: { enabled?: boolean }) {
    if (!opts?.enabled) return;
    const yScale = chart.scales['y'];
    if (!yScale) return;
    const y = yScale.getPixelForValue(0);
    const { left, right } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.strokeStyle = COLOR.zeroLine;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  },
};

Chart.register(...registerables, zoomPlugin);

@Component({
  selector: 'app-thematic-innovation-activity',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, LoadingProgressComponent],
  templateUrl: './thematic-innovation-activity.component.html',
  styleUrl: './thematic-innovation-activity.component.css',
})
export class ThematicInnovationActivityComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() theme_params: unknown = null;
  @Input() printMode = false;                              // hides chips/toolbar for the PDF report host
  @Input() lockedMetric?: IThematicInnovationMetricKey;    // forces a single metric in printMode

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  readonly metricButtons = (Object.keys(METRIC_OPTIONS) as IThematicInnovationMetricKey[]).map((key) => ({
    key,
    label: METRIC_OPTIONS[key].label,
    color: METRIC_OPTIONS[key].color,
  }));
  selectedMetric: IThematicInnovationMetricKey = 'inv_growth_absolute_12m';

  data: IThematicInnovationActivityPoint[] = [];
  chartType: 'bar' | 'line' = 'bar';
  chartjsData!: ChartConfiguration<'bar' | 'line'>['data'];
  chartjsOptions!: ChartConfiguration<'bar' | 'line'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBackgroundPlugin, zeroLinePlugin];
  currentYearLabel: string | null = null;
  dateEom: string | null = null;

  get currentMetricFootnote(): string {
    return METRIC_OPTIONS[this.selectedMetric].footnote;
  }

  readonly LOADING_STAGES = LOADING_STAGES;

  showChart = false;
  showDataMissing = false;
  loadingFailed = false;
  isLoading = false;

  get theme_search_type(): string {
    return (this.theme_params as any)?.params?.theme_search_type ?? 'cluster';
  }

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.printMode && this.lockedMetric) this.selectedMetric = this.lockedMetric;
    if (changes['tabId'] || changes['query'] || changes['lockedMetric']) {
      // Wipe card scaffolding so a new tab/query starts from a clean slate.
      this.reset();
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setMetric(metric: IThematicInnovationMetricKey): void {
    if (this.selectedMetric === metric) return;
    this.selectedMetric = metric;
    this.loadData();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  /** Exact Chart.js PNG for the PDF report (native, crisp). */
  getChartImage(scale = 2): string | null {
    return this.chartDirective?.chart?.toBase64Image('image/png', scale) ?? null;
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'innovation-activity').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-innovation-activity.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private reset(): void {
    this.data = [];
    this.showChart = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
    this.isLoading = false;
  }

  private loadData(): void {
    if (!this.tabId) {
      this.reset();
      return;
    }

    const currentTabId = this.tabId;
    const cacheKey = `thematic_innovation_activity_${this.selectedMetric}`;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, cacheKey)).pipe(
      take(1),
      switchMap((cached) => {
        // Cache value is either the legacy array (older entries) or { data, date_eom }
        // (current shape). Normalize before consuming so a stale slice still renders.
        const cachedData: IThematicInnovationActivityPoint[] | undefined =
          Array.isArray(cached) ? cached : cached?.data;
        const cachedEom: string | null =
          Array.isArray(cached) ? null : (cached?.date_eom ?? null);
        if (cachedData?.length) {
          if (this.tabId === currentTabId) {
            this.loadingFailed = false;
            this.showDataMissing = false;
            this.isLoading = false;
            this.data = cachedData;
            this.dateEom = cachedEom;
            this.buildChart();
          }
          return of(null);
        }
        // Card scaffolding stays rendered at all times; the loading overlay
        // sits on top of whatever's currently in the chart container.
        this.isLoading = true;
        this.loadingFailed = false;
        this.showDataMissing = false;
        const p = (this.theme_params as any)?.params ?? {};
        return this.apiService.getThematicInnovationActivity({
          cluster_id: p.cluster_id ?? null,
          cluster_name: p.cluster_name ?? null,
          query: this.query,
          theme_search_type: p.theme_search_type ?? 'cluster',
          stat_term: [this.selectedMetric],
          tabId: currentTabId,
        }).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            const data = payload?.data ?? [];
            if (!res.ok || !data.length) {
              if (this.tabId === currentTabId) {
                this.isLoading = false;
                this.showChart = false;
                this.loadingFailed = false;
                this.showDataMissing = true;
              }
              return;
            }
            const targetTabId = payload?.tabId ?? currentTabId;
            const dateEom = payload?.date_eom ?? null;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: cacheKey, data: { data, date_eom: dateEom } as any }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.data = data;
              this.dateEom = dateEom;
              this.buildChart();
              this.isLoading = false;
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.isLoading = false;
          this.showChart = false;
          this.showDataMissing = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  private buildChart(): void {
    const cfg = METRIC_OPTIONS[this.selectedMetric];
    const sorted = [...this.data]
      .filter((p) => p.date_publication)
      .sort((a, b) => a.date_publication.localeCompare(b.date_publication));

    if (!sorted.length) {
      this.showDataMissing = true;
      return;
    }

    // Category axis: each bar gets a slot labeled with its source year — chart.js
    // doesn't reposition them based on calendar boundaries, so date_publication of
    // "2024-12-31" stays under the "2024" tick (vs. drifting onto "2025" with a time axis).
    // Only the current (in-progress) calendar year is partial; older points are complete.
    const todayYear = new Date().getUTCFullYear();
    const lastIdx = sorted.length - 1;
    const lastYear = new Date(sorted[lastIdx].date_publication).getUTCFullYear();
    const lastIsPartial = lastYear === todayYear;

    const partialLabel = this.dateEom ? `${formatMonthYear(this.dateEom)}*` : null;
    const labels = sorted.map((p, i) => {
      const y = String(new Date(p.date_publication).getUTCFullYear());
      if (lastIsPartial && i === lastIdx) return partialLabel ?? `${y}*`;
      return y;
    });
    const values = sorted.map((p) => (p[this.selectedMetric] as number | null) ?? null);
    const numericY = values.filter((v): v is number => typeof v === 'number');

    const barCount = Math.max(sorted.length, 1);
    const dynamicBarGap = Math.min(0.75, Math.max(0.35, 0.82 - barCount * 0.035));

    // Last bar (current/partial year) gets a lighter shade; footnote explains why.
    this.currentYearLabel = lastIsPartial ? labels[lastIdx] : null;
    const baseAlpha = cfg.opacity ?? 0.9;
    const barColors = labels.map((_, i) =>
      this.hexWithAlpha(cfg.color, lastIsPartial && i === lastIdx ? baseAlpha * 0.4 : baseAlpha),
    );

    this.chartType = cfg.chartType === 'bar' ? 'bar' : 'line';

    if (cfg.chartType === 'bar') {
      this.chartjsData = {
        labels,
        datasets: [{
          label: cfg.label,
          data: values,
          backgroundColor: barColors,
          borderColor: cfg.color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          categoryPercentage: 0.85,
          barPercentage: Math.max(0.25, 1 - dynamicBarGap),
        }] as any,
      };
    } else {
      this.chartjsData = {
        labels,
        datasets: [{
          label: cfg.label,
          data: values,
          borderColor: cfg.color,
          borderWidth: 2,
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: cfg.color,
          fill: 'origin',
          backgroundColor: 'rgba(245, 158, 11, 0.12)',
        }] as any,
      };
    }

    const isGrowth = this.selectedMetric === GROWTH_METRIC;
    const yScale: any = {
      position: 'left',
      title: {
        display: true,
        text: cfg.yTitle,
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        maxTicksLimit: 11,
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
      },
      grid: { display: this.printMode, color: COLOR.grid },
      border: { display: false },
    };

    if (isGrowth && numericY.length) {
      const maxAbs = Math.max(...numericY.map((v) => Math.abs(v)));
      const paddedAbs = Math.max(10, Math.ceil(maxAbs * 1.15));
      yScale.min = -paddedAbs;
      yScale.max = paddedAbs;
    }

    this.chartjsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
      animation: {
        duration: 900,
        easing: 'easeOutQuart',
      },
      animations: {
        y: {
          type: 'number',
          duration: 900,
          easing: 'easeOutQuart',
          from: (ctx: any) => {
            if (ctx.type !== 'data' || ctx.mode !== 'default') return undefined;
            return ctx.chart.scales['y']?.getPixelForValue(0);
          },
        },
        x: { duration: 0 },
      },
      transitions: {
        active: { animation: { duration: 0 } },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: COLOR.tooltipBg,
          titleColor: COLOR.tooltipTitle,
          titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
          bodyColor: COLOR.tooltipBody,
          bodyFont: { family: FONT_FAMILY, size: 15, weight: 600 },
          padding: 12,
          borderColor: COLOR.tooltipBorder,
          borderWidth: 1,
          displayColors: false,
          cornerRadius: 8,
          caretSize: 0,
          callbacks: {
            title: (items: any[]) => items[0]?.label ?? '',
            label: (item: any) => {
              const v = item.parsed.y;
              if (v == null) return '';
              return `${Math.round(v)}${cfg.hoverSuffix ?? ''}`;
            },
          },
        },
        // Custom plugin — enabled only for the growth metric
        innovActivityZeroLine: { enabled: isGrowth },
        zoom: {
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'shift',
          },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: true },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'rgba(99, 102, 241, 0.6)',
              borderWidth: 1,
            },
            mode: 'x',
          },
        },
      } as any,
      scales: {
        x: {
          type: 'category',
          ticks: {
            maxRotation: 0,
            font: { family: FONT_FAMILY, size: 14 },
            color: COLOR.axisText,
            padding: 8,
          },
          grid: { display: false },
          border: { display: false },
        },
        y: yScale,
      },
    };

    this.showChart = true;
    this.showDataMissing = false;
    this.loadingFailed = false;
  }

  private hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
